import { memo, useCallback, useImperativeHandle, useState, Ref } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { toast } from 'react-toastify';
import { useAvailableGroups } from '../../../../hooks/QueryBuilder';
import ExistingQueriesPicker from './ExistingQueriesPicker';
import TargetingResultPanel from './TargetingResultPanel';
import CountWaitingDialog from './CountWaitingDialog';
import CashdeskTargetPicker, { CashdeskTargetValue } from './CashdeskTargetPicker';
import QueryBuilderPanel from './targeting/QueryBuilderPanel';
import TargetingSummaryAlert from './targeting/TargetingSummaryAlert';
import { useTargetingBuilder } from './targeting/useTargetingBuilder';
import { useTargetingCount } from './targeting/useTargetingCount';
import { formatRecipients } from './targeting/toasts';

type TargetingSourceTab = 'new' | 'existing-query' | 'cashdesk';

export interface TargetingFormData {
  title: string;
  description: string;
  queryId: number | null;
  queryDescription: string;
  nbRecipients: number | null;
  listId: string | number | null;
  cashdeskTarget: CashdeskTargetValue | null;
}

export interface TargetingStepHandle {
  hasPendingTargeting: () => boolean;
  launchCount: () => Promise<boolean>;
}

interface CampaignTargetingStepProps {
  data: TargetingFormData;
  onChange: (patch: Partial<TargetingFormData>) => void;
  isCashdesk: boolean;
  ref?: Ref<TargetingStepHandle>;
}

const EMPTY_CASHDESK_TARGET: CashdeskTargetValue = { targetCategoryId: null, identifierTypeId: null, entityIds: [] };

const CLEARED_SELECTION: Partial<TargetingFormData> = {
  queryDescription: '', queryId: null, nbRecipients: null, listId: null, cashdeskTarget: null,
};

function CampaignTargetingStep({ data, onChange, isCashdesk, ref }: CampaignTargetingStepProps) {
  const builder = useTargetingBuilder(data.queryDescription);
  const { count, counting, waitingQueryId, stopWaiting } = useTargetingCount(builder, onChange);
  const { groups: availableGroups, loading: loadingGroups, refetch: refetchGroups } = useAvailableGroups();
  const [sourceTab, setSourceTab] = useState<TargetingSourceTab>(data.cashdeskTarget ? 'cashdesk' : 'new');

  const hasSelection = data.queryId !== null || data.listId !== null || data.cashdeskTarget !== null;
  const resultQueryId = data.listId === null && data.cashdeskTarget === null ? data.queryId : null;

  useImperativeHandle(ref, () => ({
    hasPendingTargeting: () => sourceTab === 'new' && builder.hasRules && !hasSelection,
    launchCount: async () => (await count(false)) === 'done',
  }), [sourceTab, builder.hasRules, hasSelection, count]);

  const handleReset = () => {
    builder.reset();
    onChange(CLEARED_SELECTION);
  };

  const handleNewTargeting = () => {
    handleReset();
    setSourceTab('new');
  };

  const handleSelectExistingQuery = useCallback((query: { queryId: number; description: string; nbRecipients: number | null }) => {
    onChange({ queryId: query.queryId, queryDescription: query.description, nbRecipients: query.nbRecipients, listId: null, cashdeskTarget: null });
  }, [onChange]);

  const handleResultCount = useCallback((nbRecipients: number) => {
    if (data.nbRecipients !== nbRecipients) onChange({ nbRecipients });
  }, [data.nbRecipients, onChange]);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
        Requête de ciblage
      </Typography>

      {resultQueryId !== null ? (
        <TargetingResultPanel
          queryId={resultQueryId}
          description={data.queryDescription}
          onNewTargeting={handleNewTargeting}
          onCount={handleResultCount}
        />
      ) : (
        <>
          {hasSelection && <TargetingSummaryAlert data={data} onModify={handleReset} />}

          <Tabs
            value={sourceTab}
            onChange={(_, tab: TargetingSourceTab) => setSourceTab(tab)}
            sx={{ mb: 2, minHeight: 40, '& .MuiTab-root': { textTransform: 'none', minHeight: 40 } }}
          >
            <Tab value="new" label="Nouvelle requête" icon={<AddCircleOutlineIcon fontSize="small" />} iconPosition="start" />
            <Tab value="existing-query" label="Mes requêtes" icon={<ListAltIcon fontSize="small" />} iconPosition="start" />
            {isCashdesk && (
              <Tab value="cashdesk" label="Requête spécifique Caisse" icon={<PointOfSaleIcon fontSize="small" />} iconPosition="start" />
            )}
          </Tabs>

          {sourceTab === 'new' && (
            <QueryBuilderPanel
              builder={builder}
              defaultDescription={data.queryDescription}
              availableGroups={availableGroups}
              loadingGroups={loadingGroups}
              onRefetchGroups={refetchGroups}
              onReset={handleReset}
              onCount={() => count(true)}
              counting={counting}
            />
          )}

          {sourceTab === 'existing-query' && (
            <ExistingQueriesPicker
              selectedQueryId={data.queryId}
              availableGroups={availableGroups}
              loadingGroups={loadingGroups}
              fields={builder.advanced.fields}
              categories={builder.advanced.categories}
              onRefetchGroups={refetchGroups}
              onSelect={handleSelectExistingQuery}
            />
          )}

          {sourceTab === 'cashdesk' && isCashdesk && (
            <CashdeskTargetPicker
              value={data.cashdeskTarget ?? EMPTY_CASHDESK_TARGET}
              onChange={cashdeskTarget => onChange({
                ...CLEARED_SELECTION,
                cashdeskTarget,
                queryDescription: 'Requête spécifique Caisse',
              })}
            />
          )}
        </>
      )}

      <CountWaitingDialog
        open={waitingQueryId !== null}
        queryId={waitingQueryId}
        description={data.queryDescription}
        onCompleted={nbRecipients => {
          onChange({ nbRecipients });
          stopWaiting();
          toast.success(nbRecipients !== null ? `Requête comptée : ${formatRecipients(nbRecipients)}` : 'Comptage terminé');
        }}
        onClose={stopWaiting}
      />
    </Box>
  );
}

export default memo(CampaignTargetingStep);
